import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useModel, useStateManager, useUIState } from "@bcm-dev/bcms-vite";
import Classes, { Account } from "model";
import React from "react";

import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { CenterContentContainer, ContentHeaderView, PageContainer, PageContentView, TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableItemView, TableView } from "components/shared";
import { PrimaryButton, SecondaryButton } from "components/ui";

import { InfoBanner } from "components/shared/views/ConfirmBannerView";
import CustomContentHeaderWidget from "components/shared/views/CustomContentHeaderWidget";
import "./styles.css";
import "./mobile.css";


/**
 * @function StatementPreferencePage
 * @return {React.Component}
 * @exported true
 * @description The main component for StatementPreferencePage
 * @example
 * <StatementPreferencePage />
 */
export default function StatementPreferencePage(props) {
    const globalController = useBCMSContext();
    const { branding } = useModel();
    BCMSRecord.globalControllerFor(Classes, globalController);

    const data = { ...getDefaultPageData(branding), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };
    const className = props.className?(" "+props.className):"";

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onUpdate: id => value => {
            const accounts = state.data.accounts.map(item => {
                if(item.id === id) { item.statementPreference = value; }
                return item;
            }, []);

            BCMSStateManagerCRUD.init(state)
                .update([ "data", "accounts" ], accounts)
                .submit(setState);
        },

        onCancel: evt => {
            BCMSStateManagerCRUD.init(state)    
                .update([ "data", "accounts" ], Account.initAll())
                .submit(setState);
        }
    });

    return (
        <PageContainer className={"statement-preference-page"+className} state={ { data, ui } } controller={ controller }>
            <StatementPreferenceContent />
        </PageContainer>
    );
}

export const getDefaultPageData = (branding) => {
    const options = branding ? branding.options : {};
    const deliveryOptionsBranding = options.delivery_options_abi22 === "undefined" || options.delivery_options_abi22 === undefined
    ? "Paper,Electronic"
    : options.delivery_options_abi22;
    let deliveryOptions = deliveryOptionsBranding.split(",");

    if(deliveryOptions.length === 1) {
        deliveryOptions = [ "Other", ...deliveryOptions ];
    }
    
    deliveryOptions = deliveryOptions.reduce((acc, option, index) => {
        acc.push({
            label: option,
            index: index,
            isDemoDefaultOption: option === "Other" && index === 0
        });
        return acc;
    }, []);

    const deliveryOptionsHasPaper = deliveryOptions.filter(item => item.label === "Paper").length > 0;
    const deliveryOptionsPaperIndex = deliveryOptions.findIndex(item => item.label === "Paper");

    // A multiple values branding option with "Paper" is a special case where the first select 
    // will be set to "Paper" and the rest of the selects will
    // be set to the next value after "Paper" in the multiple values option.
    // If "Paper" is not the first value in the array of values, 
    // then the rest of the selects will be set to the first value in the array.
    /* 
    Example 1:
    For first select, avail options are "Paper -> Option 2 -> Option 3" ... "Paper" would be selected.
    For second/third/fourth selects, avail "options are Paper -> Option 2 -> Option 3" ... "Option 2" would be selected.

    Example 2:
    For first select, avail options are "Option 1 -> Option 2 -> Paper" ... "Paper" would be selected.
    For second/third/fourth selects, avail options are "Option 1 -> Option 2 -> Paper" ... "Option 1" would be selected.
    */
    const accounts = Account.initAll().reduce((acc, item, index) => {
        if(index === 0) {
            if(deliveryOptionsHasPaper) {
                item.statementPreference = "Paper";
            } else {
                item.statementPreference = deliveryOptions[0].label;
            }
        } else {
            if(deliveryOptionsHasPaper) {
                if(deliveryOptionsPaperIndex > 0) {
                    item.statementPreference = deliveryOptions[0].label;
                } else {
                    item.statementPreference = deliveryOptions[1].label;
                }
            } else {
                item.statementPreference = deliveryOptions[1].label;
            }
        }

        item.currIndex = index;
        acc.push(item);
        return acc;
    }, []);

    return {
        accounts,
        deliveryOptions
    }
};

export const getDefaultPageUI = () => ({
    savedBanner: { isActive: false }
});

function StatementPreferenceContent(props) {
    const { data, ui, onUpdate, onCancel } = useStateManager();
    const { branding } = useModel();
    const { options } = branding;
    const accountItems = data.accounts.map((account, index) => {
        return <StatementPreferenceTableRow key={ `Account_${ account.id }` } account={ account } accountIndex={ index } onChange={ onUpdate(account.id) } /> 
    }, [])
    const subHeaderContent = options.statement_preference_subheader_abi22; 
    const promoText = options.statement_preference_promo_abi22;
    const subfooterText = options.statementpreference_subfooter_abi22;

    return (
        <PageContentView>
            {/* InfoBanner is only shown in captures NOT in click-thru */}
            { ui.savedBanner.isActive && <InfoBanner text="Your statement preference has been saved." dataPoint="Statement_Preference_Banner" /> }
            <div className="flex-container flex-vertical page-widget-wrapper">
                {promoText && <CustomContentHeaderWidget content = {promoText}/>}
                <CenterContentContainer>
                    <ContentHeaderView text="Statement Preference" />
                    {subHeaderContent && <CustomContentHeaderWidget content = {subHeaderContent}/>}
                    <TableView className="statement-preference-table">
                        <TableColumnContainer>
                            <TableColumnView className="text-left account-nickname-column" text="Account Nickname" />
                            <TableColumnView className="text-left account-number-column" text="Account Number" />
                            <TableColumnView className="text-left delivery-option-column" text="Delivery Option" />
                        </TableColumnContainer>
                        
                        <TableBody>
                            { accountItems }
                        </TableBody>
                    </TableView>
                    
                    <div className="flex-container flex-horizontal align-center button-container">
                        <SecondaryButton text="Cancel" onClick={ onCancel } />

                        <DisabledPrompt>
                            <PrimaryButton text="Save" dataPoint="Statement_Preference_Save" />
                        </DisabledPrompt>
                    </div>
                </CenterContentContainer>
                { subfooterText && <CustomContentHeaderWidget content={ subfooterText } /> }
            </div>
        </PageContentView>
    );
}

/**
 * @function StatementPreferenceTableRow
 * @param {Account} account - The account use to fill out information
 * @return {React.Component}
 * @exported false
 * @description The StatementPreferenceTableRow is one row in the table shown on  StatementPreferencePage.
 * @example
 * <StatementPreferenceTableRow account={ account } />
 */

function StatementPreferenceTableRow(props) {
    const { data, ui, onUpdate, onCancel } = useStateManager();
    const account = props.account ?? Account.firstInstanceOf();
    const { key, value } = getStatementPreferenceItem(account.id);
    const { config } = useUIState(key, value);

    const onChange = value => props.onChange instanceof Function
    ? props.onChange(value)
    : undefined;

    let options =  [ ...data.deliveryOptions ];
    const includesDemoDefaultOption = data.deliveryOptions.filter( item => item.isDemoDefaultOption).length > 0;
    
    // If the demo "Other" option is set, only show that option for the first account select 
    // but not the rest of the account selects.
    if(account.currIndex > 0 && includesDemoDefaultOption) options = options.slice(1);

    const items = options.reduce((acc, option) => {
        acc.push(<CustomSelectItem key={ option.index } dataPoint={ `Preference_${ account.id }_${ option.index }` }>{ option.label }</CustomSelectItem>);
        return acc;
    }, []);

    return (
    <TableItemContainer>
        <TableItemTextView className="account-nickname-column" text={account.nickname} />
        <TableItemTextView className="account-number-column" text={account.number} />
        <TableItemView className="delivery-option-column">
            <CustomSelect value={ account.statementPreference } active={ config.selectIsActive } onChange={onChange} dataPoint={ `Preferece_Select_${ account.id }` }>
                { items }
            </CustomSelect>
        </TableItemView>
    </TableItemContainer>
    );
}

export const getStatementPreferenceItem = id => ({
  key: `Preference_${ id }`,
  value: {
    selectIsActive: false
  }
});